/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, { useState, Fragment } from 'react';
import { Box, Typography, Divider, FormHelperText } from '@material-ui/core';
import useContentMgmt from '../../../../../hooks/useContentMgmt';
import colors from '../../../../../themes/main-colors';
import { styled } from '@material-ui/core/styles';
import RadioButtonGroup from '../../../../pge-plus-common/RadioButtonGroup';
import microcopyGroupIds from '../../../../pge-plus-common/microcopyGroupIds';
import { Question } from '../../../../pge-plus-common/Typography';
import theme from '../../../../../themes/theme';
import useFormState from '../../../../../hooks/useFormState';
import TextField from '../../../../text-field';
import { RouteComponentProps } from '@reach/router';
import { ProceedOrCancelButton } from '../../../../pge-plus-common/StepperForm';
import routes from '../../../../../routes';
import {
  EVPulseSmartChargingEnrollment,
  UpdateSectionErrorCode,
} from '../rebateOnlyForm.types';
import { evPulseEnrollmentValidateFunction } from '../rebateOnlyForm.rules';
import { Link, navigate } from 'gatsby';
import FileUploaderWithOCR from '../../../../pge-plus-common/FileUploaderWithOCR';
import { useRebateOnlyFormState } from '../../../../../providers/RebateOnlyFormStateProvider';
import useUpdateERSections from '../../../../../hooks/pge-plus/useUpdateERSections';
import {
  ApplicationErSectionType,
  ApplicationSectionStatus,
  OcrFieldNames,
  SectionErRebateInfo,
} from '../../../../../__generated__/pgeplus-types';
import useWrapWithLoader from '../../../../../hooks/useWrapWithLoading';
import { rebateInfoERFormStateAdapter } from '../rebateOnlyFormState.adapter';
import useUtil from '../../../../need-more-time-to-pay/useUtil';
import { SaveAndComeBackLink } from '../../../../pge-plus-common/SaveAndComeBackLink';
import PopoverToolTip from '../../../../pge-plus-common/PopoverToolTip';
const PREFIX = 'evPulse';
const classes = {
  submit: `${PREFIX}-submit`,
  errorMessage: `${PREFIX}-errorMessage`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled(Box)(({ theme: theme2 }) => ({
  [`& .${classes.submit}`]: {
    backgroundColor: colors.orange,
    borderColor: colors.orange,
    '&:hover': {
      backgroundColor: colors.orange,
      borderColor: colors.orange,
    },
    [theme2.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  [`& .${classes.errorMessage}`]: {
    fontFamily: 'Untitled-Sans',
    fontSize: '1rem !important',
  },
}));

const SmartChargingEnrollForTesla: React.FC<RouteComponentProps> = () => {
  const { content, inlineRichText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_REBATE_ONLY_EV_PULSE_ENTROLLMENT,
  );
  const { content: fileUploaderText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_UPLOAD_PHOTOS,
  );
  const [ocrExtractionStatus, setOcrExtractionStatus] = useState<
    string | undefined
  >();

  const { wrapWithLoader } = useWrapWithLoader();
  const {
    formState: { rebateInfo, application },
    getSectionData,
    updateFormState,
  } = useRebateOnlyFormState();

  const form = useFormState(rebateInfo.evPulse!, {
    validate: evPulseEnrollmentValidateFunction(),
    validationContext: {
      content,
    },
  });
  const { updateSectionRebateInfo } = useUpdateERSections();
  const sectionDataRebateInfo: SectionErRebateInfo = getSectionData(
    ApplicationErSectionType.ErRebateInfo,
  );
  const subText1 = {
    fontSize: 20,
    marginBottom: theme.spacing(2),
  };

  const boldText = {
    fontWeight: 700,
  };

  const { setErrorNotification } = useUtil();

  const onSubmit = wrapWithLoader(
    async (data: EVPulseSmartChargingEnrollment) => {
      try {
        const response = await updateSectionRebateInfo(
          rebateInfoERFormStateAdapter().fromFormState(
            {
              ...rebateInfo,
              evPulse: {
                ...data,
                vin: data?.doYouOwnL2Charger ? data?.vin : undefined,
                vinImage: data?.doYouOwnL2Charger ? data?.vinImage : undefined,
              },
              qualifiedProductInfo: undefined,
            },
            application?.id!,
            sectionDataRebateInfo?.id,
            ApplicationSectionStatus.Completed,
          ),
        );

        // Check for errors to show the red banner and block navigation
        if (
          response?.errors ||
          response.data?.updateSectionERRebateInfo?.validation?.errors
        ) {
          throw new Error();
        }

        //Store the data in global store
        if (response.data?.updateSectionERRebateInfo?.section.data) {
          const updatedValues = rebateInfoERFormStateAdapter().toFormState(
            response.data?.updateSectionERRebateInfo?.section.data,
          );
          updateFormState('rebateInfo', updatedValues);
        }

        if (!response.data?.updateSectionERRebateInfo.section.errors.length) {
          void navigate(routes.PGE_PLUS_REBATE_ONLY_CUSTOMER_INFORMATION);
        } else {
          const ineligibleErrors = response.data?.updateSectionERRebateInfo.section.errors.filter(
            reason =>
              reason.code === UpdateSectionErrorCode.DoesNotDriveTesla ||
              reason.code === UpdateSectionErrorCode.DoesNotHaveL2Charger ||
              reason.code === UpdateSectionErrorCode.VINAlreadyEnrolled,
          );
          if (ineligibleErrors) {
            const errorCode = ineligibleErrors[0].code;
            const messageState =
              errorCode === UpdateSectionErrorCode.DoesNotDriveTesla
                ? {
                    message: 'REBATE_ONLY_TIME_OF_THE_DAY',
                  }
                : errorCode === UpdateSectionErrorCode.VINAlreadyEnrolled
                ? {
                    message: 'REBATE_ONLY_SMART_CHARGING_INELIGIBLE',
                  }
                : {
                    message: 'REBATE_ONLY_L2_CHARGER_REQUIRED',
                  };

            void navigate(routes.PGE_PLUS_REBATE_ONLY_INELIGIBLE, {
              state: messageState,
            });
            return;
          } else {
            throw new Error();
          }
        }
      } catch (e) {
        setErrorNotification(true);
      }
    },
  );

  const handleSaveAndExit = wrapWithLoader(async () => {
    try {
      const response = await updateSectionRebateInfo({
        ...rebateInfoERFormStateAdapter().fromFormState(
          {
            ...rebateInfo,
            evPulse: {
              ...form.values,
              vin: form.values?.doYouOwnL2Charger
                ? form.values?.vin
                : undefined,
              vinImage: form.values?.doYouOwnL2Charger
                ? form.values?.vinImage
                : undefined,
            },
            qualifiedProductInfo: undefined,
          },
          application?.id!,
          sectionDataRebateInfo?.id,
        ),
        saveAndComeBack: true,
      });
      if (
        response?.errors ||
        response.data?.updateSectionERRebateInfo?.validation?.errors ||
        response.data?.updateSectionERRebateInfo?.section?.errors.length !== 0
      ) {
        throw new Error();
      } else {
        void navigate(routes.ACCOUNT);
      }
    } catch {
      setErrorNotification(true);
    }
  });

  return (
    <Root>
      <form
        onSubmit={form.submit(values => onSubmit(values), console.log)}
        noValidate
      >
        <Box
          marginBottom={2}
          css={css`
            & a {
              text-decoration: none;
            }
          `}
        >
          <Question
            detail={
              <Fragment>
                <Typography
                  css={{
                    fontSize: '20px',
                    '&>a': { textDecoration: 'none', fontWeight: 700 },
                  }}
                >
                  {inlineRichText(
                    content.get(
                      'REBATE_ONLY_ONLY_EV_CHARGERS_ON_LIST_QUALIFY_DETAILS',
                    ),
                  )}

                  <PopoverToolTip
                    popoverContent={content.get(
                      'REBATE_ONLY_LEARN_MORE_LIMITED_QUALIFYING_EV_CHARGERS_DETAILS',
                    )}
                  />
                </Typography>
              </Fragment>
            }
          />
        </Box>
        <Box
          css={css`
            & a {
              text-decoration: none;
            }
          `}
        >
          <Question
            detail={
              <Fragment>
                <Typography style={{ fontSize: '20px' }}>
                  {content.get('REBATE_ONLY_TESLA_SMART_CHARGING_PROGRAM_INFO')}
                  <PopoverToolTip
                    popoverContent={content.get(
                      'REBATE_ONLY_TESLA_LEARN_MORE_OFFER',
                    )}
                  />
                </Typography>
              </Fragment>
            }
          />
        </Box>
        <Box>
          <Question
            title={content.get('REBATE_ONLY_DO_YOU_DRIVE_TESLA_HEADER')}
          />
          <Box
            mt={3}
            css={{
              width: 'max-content',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            <RadioButtonGroup
              options={[
                {
                  label: content.get('REUSABLE_BUTTON_YES') || '',
                  value: true,
                },
                {
                  label: content.get('REUSABLE_BUTTON_NO') || '',
                  value: false,
                },
              ]}
              state={form.values?.doYouOwnTesla}
              setState={e => form.setValue('doYouOwnTesla', e as boolean)}
            />

            {form.errors?.doYouOwnTesla && (
              <FormHelperText error>
                {form.errors?.doYouOwnTesla}
              </FormHelperText>
            )}
          </Box>
          <Divider css={{ color: colors.gray93, margin: '30px 0 0px 0' }} />
        </Box>

        {form.values?.doYouOwnTesla && (
          <Fragment>
            <Box>
              <Question
                title={content.get(
                  'REBATE_ONLY_YOU_MAY_BE_ELIGIBLE_SMART_CHARGING_HEADER',
                )}
                detail={content.get('REBATE_ONLY_SINCE_YOU_DRIVE_TESLA')}
              />
            </Box>

            <Box>
              <Question
                title={content.get('REBATE_ONLY_YOU_NEED_L2_CHARGER_HEADER')}
                popoverLinkContent={'Learn More'}
                popoverContent={content.get('REBATE_ONLY_L2_LEARN_MORE')}
              />
              <Box
                mt={3}
                css={{
                  width: 'max-content',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
              >
                <RadioButtonGroup
                  options={[
                    {
                      label: content.get('REUSABLE_BUTTON_YES') || '',
                      value: true,
                    },
                    {
                      label: content.get('REUSABLE_BUTTON_NO') || '',
                      value: false,
                    },
                  ]}
                  state={form.values.doYouOwnL2Charger}
                  setState={e => form.setValue('doYouOwnL2Charger', e)}
                />
                {form.errors.doYouOwnL2Charger && (
                  <FormHelperText error>
                    {form.errors.doYouOwnL2Charger}
                  </FormHelperText>
                )}
              </Box>
              <Divider css={{ color: colors.gray93, margin: '30px 0 0px 0' }} />
            </Box>

            {form.values.doYouOwnL2Charger && (
              <Box mt={3}>
                <Question
                  title={content.get('REBATE_ONLY_TAKE_PHOTO_TESLA_VIN_HEADER')}
                  detail={content.get(
                    'REBATE_ONLY_WHERE_TO_FIND_TESLA_VIN_DETAILS',
                  )}
                />
                <Box mt={2}>
                  <FileUploaderWithOCR
                    image={form.values?.vinImage}
                    placeholder={fileUploaderText.get('UPLOAD_A_PHOTO')}
                    reuploadLabel={fileUploaderText.get('REUPLOAD_PHOTO')!}
                    ocrType={OcrFieldNames.Vin}
                    onComplete={(key, extractedText, message, status) => {
                      void form.setValue('vinImage', { id: key });
                      void form.setValue('vin', extractedText);
                      setOcrExtractionStatus(status);
                    }}
                  />
                  {form.errors.vinImage && (
                    <FormHelperText className={classes.errorMessage} error>
                      {form.errors.vinImage}
                    </FormHelperText>
                  )}
                  {form.values?.vinImage && (
                    <Box
                      css={{
                        marginBottom: theme.spacing(1),
                        '& .MuiFormControl-root': {
                          background: 'transparent',
                        },
                        '& .MuiInputBase-input': {
                          background: '#FFF',
                        },
                      }}
                    >
                      {ocrExtractionStatus && ocrExtractionStatus === 'fail' && (
                        <FormHelperText
                          className={classes.errorMessage}
                          error
                          component="div"
                        >
                          {fileUploaderText.get('OCR_FAIL_MESSAGE')}
                        </FormHelperText>
                      )}
                      <Typography css={{ ...subText1, ...boldText }}>
                        {ocrExtractionStatus &&
                        ocrExtractionStatus === 'success'
                          ? content.get('REBATE_ONLY_EDIT_VIN_HEADER')
                          : content.get('VIN')}
                      </Typography>
                      <TextField
                        css={{
                          background: 'transparent',
                          '.MuiInputBase-formControl': {
                            background: colors.white,
                          },
                          width: '100%',
                        }}
                        name={'vin'}
                        {...form.props('vin')}
                      />

                      {ocrExtractionStatus &&
                        ocrExtractionStatus === 'success' && (
                          <Typography css={{ marginTop: 8 }}>
                            {fileUploaderText.get('VIN_SUCCESS_MESSAGE')}
                          </Typography>
                        )}
                    </Box>
                  )}
                </Box>

                <Divider
                  css={{ color: colors.gray93, margin: '30px 0 30px 0' }}
                />
              </Box>
            )}
          </Fragment>
        )}
        <ProceedOrCancelButton
          back={
            sectionDataRebateInfo?.data?.rebateInfo?.existingEnrollments &&
            sectionDataRebateInfo?.data?.rebateInfo?.existingEnrollments
              .evPulse &&
            sectionDataRebateInfo?.data?.rebateInfo?.existingEnrollments.evPulse
              ?.length > 0 &&
            !rebateInfo.evPulseEnrollmentConfirmation
              ?.willEnrollNewSmartChargingDevice &&
            rebateInfo.evPulseEnrollmentConfirmation?.willEnrollNewVehicle
              ? routes.PGE_PLUS_REBATE_ONLY_VERIFY_EVPULSE_REBATE
              : routes.PGE_PLUS_REBATE_ONLY_UPLOAD_MODEL_NUMBER
          }
          disable={
            form.values?.doYouOwnTesla === undefined ||
            (form.values?.doYouOwnTesla &&
              form.values?.doYouOwnL2Charger === undefined) ||
            (form.values?.doYouOwnTesla &&
              form.values?.doYouOwnL2Charger &&
              !form.values?.vin)
          }
        />
      </form>
      <Box mt={6}>
        <SaveAndComeBackLink
          content={content}
          onSaveAndExit={handleSaveAndExit}
        />
      </Box>
    </Root>
  );
};

export default SmartChargingEnrollForTesla;
